import { faInfoCircle, faSearch } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import moment from 'moment'
import React, { useState } from 'react'
import {
  Badge,
  Card,
  CardBody,
  CardFooter,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  Table,
  UncontrolledDropdown
} from 'reactstrap'
import Swal from 'sweetalert2'
import {
  DELETION_SOURCE_LABELS,
  DELETION_STATUS_COLORS,
  DELETION_STATUS_LABELS,
  deleteAccountDeletionRequest,
  fetchAccountDeletionRequests
} from '../../../helpers/api/accountDeletion'
import { SUCCESS_RESPONSE } from '../../../helpers/codeResponses/codes'
import handleErrorMessage from '../../../helpers/errors/handleErrorMessage'
import useSWRFetchPagination from '../../../helpers/hooks/useSWRFetchPagination'
import { isIterableArray } from '../../../helpers/utils'
import ButtonIcon from '../../common/ButtonIcon'
import FalconCardHeader from '../../common/FalconCardHeader'
import PageHeader from '../../common/PageHeader'
import FullPagination from '../../common/pagination/FullPagination'
import { Message, Or, PreloadState } from '../../UI/preloading/PreloadState'
import { Toast } from '../../UI/toast/Toast'
import AccountDeletionDetailsModal from './components/AccountDeletionDetailsModal'

const AccountDeletionRequests = () => {
  const [textSearch, setTextSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [params, setParams] = useState({
    search: null,
    status: ''
  })

  const {
    data,
    isLoading,
    error,
    mutate,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage }
  } = useSWRFetchPagination(fetchAccountDeletionRequests, params)

  const applySearch = (value = textSearch) => {
    const term = value.trim()
    setTextSearch(term)
    setParams((rest) => ({ ...rest, search: term || null }))
  }

  // Solo saca la solicitud del listado (soft delete): no elimina la cuenta
  // del usuario, esa parte la ejecuta el equipo manualmente.
  const removeFromList = (id) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: '¿Quieres quitar esta solicitud del listado?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#9341af',
      cancelButtonColor: '#bbb',
      confirmButtonText: 'Sí, quitar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.value) {
        try {
          const { code } = await deleteAccountDeletionRequest(id)
          if (code === SUCCESS_RESPONSE.OK) {
            Toast.fire({ type: 'success', title: 'Solicitud quitada del listado.' })
            mutate()
          }
        } catch (error) {
          handleErrorMessage(error)
        }
      }
    })
  }

  return (
    <>
      <PageHeader
        title="Eliminación de cuentas"
        titleTag="h2"
        description="Gestiona las solicitudes de eliminación de cuenta e información enviadas desde la web y las apps móviles."
        className="mb-3"
        col={{ lg: 9 }}
      />

      <Card>
        <FalconCardHeader title="Solicitudes">
          <Row>
            <Col xs={12} md={''} className="d-flex mb-2 mb-md-0">
              <Input
                type="select"
                value={params.status}
                onChange={({ target }) =>
                  setParams((rest) => ({ ...rest, status: target.value }))
                }
                className="d-inline-block mr-2"
                style={{ maxWidth: '11rem' }}
              >
                <option value="">Todos los estados</option>
                {Object.entries(DELETION_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Input>
              <Input
                type="text"
                value={textSearch}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return
                  applySearch(e.currentTarget.value)
                }}
                onChange={(event) => {
                  const value = event.target.value
                  setTextSearch(value)
                  if (value.trim() === '') applySearch('')
                }}
                placeholder="Nombre, correo, teléfono, cédula, condominio, origen o estado..."
                className="d-inline-block search-input mr-2"
              />
              <ButtonIcon
                icon={faSearch}
                className="d-inline-block text-nowrap"
                color="falcon-default"
                onClick={() => applySearch()}
              >
                Buscar
              </ButtonIcon>
            </Col>
          </Row>
        </FalconCardHeader>
        <CardBody className={classNames('fs--1', { 'p-0': isIterableArray(data) })}>
          <PreloadState cond={isIterableArray(data)} loading={isLoading}>
            <Table responsive className="mb-0">
              <thead>
                <tr>
                  <th>Solicitante</th>
                  <th>Contacto</th>
                  <th>Origen</th>
                  <th>Estado</th>
                  <th>Recibida</th>
                  <th>Opciones</th>
                </tr>
              </thead>
              <tbody>
                {data?.map((request) => (
                  <tr key={request.id}>
                    <td>
                      {request.full_name}
                      {!request.has_account && (
                        <div className="text-500 fs--2">Sin cuenta asociada</div>
                      )}
                    </td>
                    <td>
                      {request.email || (
                        <span className="text-500">
                          Sin correo{request.username ? ` · ${request.username}` : ''}
                        </span>
                      )}
                    </td>
                    <td>{DELETION_SOURCE_LABELS[request.source] || request.source}</td>
                    <td>
                      <Badge color={`soft-${DELETION_STATUS_COLORS[request.status]}`} className="fs--2">
                        {DELETION_STATUS_LABELS[request.status] || request.status}
                      </Badge>
                    </td>
                    <td>{moment(request.created_at).format('DD/MM/YYYY')}</td>
                    <td>
                      <Col xs="auto" className="d-sm-flex align-items-center">
                        <UncontrolledDropdown direction="left" className="d-inline-block ml-2">
                          <DropdownToggle color="falcon-default" size="sm">
                            <FontAwesomeIcon icon="ellipsis-h" />
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem
                              onClick={() => {
                                setSelected(request)
                                setIsOpen(true)
                              }}
                            >
                              Ver y gestionar
                            </DropdownItem>
                            <DropdownItem divider />
                            <DropdownItem
                              className="text-danger"
                              onClick={() => removeFromList(request.id)}
                            >
                              Quitar del listado
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </Col>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Or value={false} cond={!!error}>
              <Message
                value={false}
                color={'info'}
                className="p-3 mr-3"
                align="left"
                icon={faInfoCircle}
              >
                No hay solicitudes de eliminación registradas con los filtros aplicados.
              </Message>
              <Message
                value={true}
                color={'danger'}
                className="p-3 mr-3"
                align="left"
                icon={faInfoCircle}
              >
                No pudimos cargar las solicitudes, por favor reintenta nuevamente.
              </Message>
            </Or>
          </PreloadState>
        </CardBody>
        {isIterableArray(data) && (
          <CardFooter className="border-top">
            <FullPagination
              nextPage={nextPage}
              prevPage={previousPage}
              goToPage={goToPage}
              meta={meta}
              className={'shadow-none'}
              posText={'solicitudes'}
              perPage={perPage}
            />
          </CardFooter>
        )}
      </Card>

      <AccountDeletionDetailsModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        request={selected}
        setSelected={setSelected}
        mutate={mutate}
      />
    </>
  )
}

export default AccountDeletionRequests
